import React, { useMemo, useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, ComposedChart, PieChart, Pie, Cell
} from 'recharts';
import {
  CheckCircle2, AlertTriangle, Clock, ClipboardList, TrendingUp, TrendingDown,
  Filter, Calendar, RefreshCcw, Sun, Moon
} from 'lucide-react';
import axios from 'axios';
import config from '../config.json'

// Types
/** @typedef {{
 *   id: string,
 *   date: string, // YYYY-MM-DD
 *   shift: string,
 *   plant: string,
 *   line: string,
 *   component: string,
 *   operation: string,
 *   inspector: string,
 *   planned: number,
 *   done: number,
 *   onTime: number,
 *   defects: number,
 *   opportunities: number,
 *   ncrCount: number,
 *   defectBreakup: Record<string, number>,
 *   overdue: number,
 *   firstPassOk: number
 * }} QCEntry */

// Utilities
const fmt = new Intl.NumberFormat("en-IN");
const pct = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : 0);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function addDays(base, d) {
  const dt = new Date(base);
  dt.setDate(dt.getDate() + d);
  return dt;
}
function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

// Mock Data Generator
function generateSampleData(days = 14) {
  const today = new Date();
  const plants = ["Faridabad", "Manesar"];
  const lines = ["Press-1", "Press-2", "Mould-1", "Mould-2"];
  const components = ["Bracket-A", "Cover-B", "Housing-C", "Clip-D"];
  const ops = ["Forming", "Punching", "Trimming", "Inspection"];
  const inspectors = ["Meera", "Arun", "Fatima", "Ravi"];
  const defectsList = ["Scratch", "Burr", "Dent", "Flash", "Short Shot", "Dim Out"];

  const rows = [];
  for (let d = days - 1; d >= 0; d--) {
    const date = toIso(addDays(today, -d));
    for (const plant of plants) {
      for (const line of lines) {
        for (const component of components) {
          const operation = ops[Math.floor(Math.random() * ops.length)];
          const inspector = inspectors[Math.floor(Math.random() * inspectors.length)];
          const planned = 4 + Math.floor(Math.random() * 5);
          const done = clamp(planned - Math.floor(Math.random() * 3), 0, planned);
          const onTime = clamp(done - Math.floor(Math.random() * 2), 0, done);
          const opportunities = done * (5 + Math.floor(Math.random() * 6));
          const defects = Math.floor(opportunities * (Math.random() * 0.01));
          const overdue = Math.max(0, planned - done);
          const firstPassOk = clamp(done - Math.floor(Math.random() * 3), 0, done);

          const defectBreakup = {};
          let rem = defects;
          while (rem > 0) {
            const k = defectsList[Math.floor(Math.random() * defectsList.length)];
            const add = Math.min(rem, 1 + Math.floor(Math.random() * 3));
            defectBreakup[k] = (defectBreakup[k] || 0) + add;
            rem -= add;
          }
          rows.push({
            id: `${date}-${plant}-${line}-${component}`,
            date, shift: ["Shift 1", "Shift 2", "Shift 3"][Math.floor(Math.random() * 3)],
            plant, line, component, operation, inspector,
            planned, done, onTime, defects, opportunities, ncrCount: Math.floor(defects * 0.2), overdue, firstPassOk, defectBreakup
          });
        }
      }
    }
  }
  return rows;
}

// Small UI Bits
const Badge = ({ children, className = "" }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${className}`}>
    {children}
  </span>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-background/5 backdrop-blur border border-border rounded-2xl p-4 shadow-sm ${className}`}>
    {children}
  </div>
);

const KPI = ({ icon: Icon, label, value, sublabel, trend, trendUp = true }) => (
  <Card>
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-xl bg-background/10">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="text-sm opacity-80">{label}</div>
        <div className="text-2xl font-semibold leading-tight">{value}</div>
        {sublabel && <div className="text-xs opacity-70 mt-0.5">{sublabel}</div>}
      </div>
      {trend != null && (
        <div className={`text-xs ${trendUp ? "text-green-400" : "text-red-400"}`}>
          {trendUp ? <TrendingUp className="inline w-4 h-4 mr-1" /> : <TrendingDown className="inline w-4 h-4 mr-1" />}
          {trend > 0 ? `+${trend}%` : `${trend}%`}
        </div>
      )}
    </div>
  </Card>
);

function groupBy(arr, keyFn) {
  return arr.reduce((acc, item) => {
    const k = keyFn(item);
    acc[k] = acc[k] || [];
    acc[k].push(item);
    return acc;
  }, {});
}

function sum(arr, sel) {
  return arr.reduce((a, b) => a + (typeof sel === 'function' ? sel(b) : (b[sel] || 0)), 0);
}

function QualityDashboard({ data: incoming, Heading }) {
  const [range, setRange] = useState({ from: toIso(addDays(new Date(), -13)), to: toIso(new Date()) });
  const [filters, setFilters] = useState({ Inspection: "All", ItemName: "All", ItemNumber: "All", Operation: "All" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [items, setItems] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [finalData, setFinalData] = useState({ totals: [{}], inspections: [], inspectionWiseTrend: [], dateTrend: [] });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    Heading("Quality Management Dashboard");
  }, [Heading]);

  useEffect(() => {
    getItems();
    getInspectionTypes();
    handleApplyData();
  }, []);

  const getItems = () => {
    axios.get(`${config.api}item`)
      .then((res) => {
        setItems(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  const handleApplyData = () => {
    axios.put(`${config.api}QualityDashboard`, { From: range.from, Till: range.to, Filter: filters })
      .then((res) => {
        setFinalData(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }



  const getInspectionTypes = () => {
    axios
      .get(config.api + "customInspection")
      .then((result) => {
        // setquery(false)
        setInspectionTypes(result.data);
      })
      .catch((err) => {
        //  setquery(false)
        console.log(err);
      })
  }

  useEffect(() => {
    handleApplyData();
  }, [refreshKey, range, filters])

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const data = useMemo(() => incoming && incoming.length ? incoming : generateSampleData(14), [incoming, refreshKey]);

  const allPlants = useMemo(() => Array.from(new Set(data.map(d => d.plant))), [data]);
  const allLines = useMemo(() => Array.from(new Set(data.map(d => d.line))), [data]);
  const allComponents = useMemo(() => Array.from(new Set(data.map(d => d.component))), [data]);
  const allInspectors = useMemo(() => Array.from(new Set(data.map(d => d.inspector))), [data]);

  const filtered = useMemo(() => {
    const from = new Date(range.from), to = new Date(range.to);
    return data.filter(d => {
      const dt = new Date(d.date);
      const inRange = dt >= from && dt <= to;
      return inRange
        && (filters.Inspection === "All" || d.inspectionType === filters.Inspection)
        && (filters.ItemName === "All" || d.itemName === filters.ItemName)
        && (filters.ItemNumber === "All" || d.itemNumber === filters.ItemNumber)
        && (filters.Operation === "All" || d.operation === filters.Operation);
    });
  }, [data, range, filters]);

  // KPI calculations
  const planned = sum(filtered, 'planned');
  const done = sum(filtered, 'done');
  const onTime = sum(filtered, 'onTime');
  const overdue = sum(filtered, 'overdue');
  const firstPassOk = sum(filtered, 'firstPassOk');
  const defects = sum(filtered, 'defects');
  const opp = sum(filtered, 'opportunities');
  const ncr = sum(filtered, 'ncrCount');

  const compliance = pct(onTime, done);
  const completion = pct(done, planned);
  const fpY = pct(firstPassOk, done);
  const dPmo = Math.round(opp > 0 ? (defects / opp) * 1_000_000 : 0);

  // Trends by date
  const byDate = useMemo(() => {
    const g = groupBy(finalData.dateTrend.filter(d => d._id && d.date), d => d._id && d.date);
    const dates = Object.keys(g).sort();
    return dates.map(date => {
      const rows = g[date];
      const pln = sum(rows, 'Planned');
      const dn = sum(rows, 'Completed');
      const ot = sum(rows, 'OnTimeRate');
      const df = sum(rows, 'defects');
      const op = sum(rows, 'opportunities');
      const fpo = sum(rows, 'PassRate');
      return {
        date,
        planned: pln,
        done: dn,
        compliance: pct(ot, dn),
        passRate: pct(fpo, dn),
        dPmo: Math.round(op > 0 ? (df / op) * 1_000_000 : 0),
      };
    });
  }, [finalData]);

  // Plan vs Actual by line
  const byLine = useMemo(() => {
    const g = groupBy(finalData.inspectionWiseTrend, d => d.InspectionName);
    return Object.keys(g).map(line => {
      const rows = g[line];
      return { line, planned: sum(rows, 'Planned'), done: sum(rows, 'Completed'), overdue: sum(rows, 'Overdue') };
    }).sort((a, b) => b.Planned - a.Planned).slice(0, 8);
  }, [finalData]);

  // Pareto of defects
  const defectPareto = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      for (const [k, v] of Object.entries(r.defectBreakup || {})) {
        map[k] = (map[k] || 0) + v;
      }
    });
    const items = Object.entries(map).map(([k, v]) => ({ name: k, count: v }))
      .sort((a, b) => b.count - a.count).slice(0, 10);
    let csum = 0;
    const total = items.reduce((a, b) => a + b.count, 0) || 1;
    return items.map(it => {
      csum += it.count;
      return { ...it, cumPct: Math.round((csum / total) * 1000) / 10 };
    });
  }, [filtered]);

  // Overdue list
  const overdueList = useMemo(() => {
    return finalData.inspections
      .sort((a, b) => b.TotalOverdue - a.TotalOverdue)
      .slice(0, 10)
      .map((r, idx) => ({
        key: idx + 1,
        inspection: r._id,
        planned: r.TotalPlanned,
        done: r.TotalCompleted,
        overdue: r.TotalOverdue,
      }));
  }, [finalData]);

  // Trend deltas
  const deltas = useMemo(() => {
    const from = new Date(range.from), to = new Date(range.to);
    const span = Math.ceil((to - from) / (1000 * 3600 * 24)) + 1;
    const prevFrom = toIso(addDays(from, -span));
    const prevTo = toIso(addDays(from, -1));
    const prev = data.filter(d => d.date >= prevFrom && d.date <= prevTo);
    const P = sum(prev, 'planned');
    const D = sum(prev, 'done');
    const OT = sum(prev, 'onTime');
    const FPO = sum(prev, 'firstPassOk');
    const DEF = sum(prev, 'defects');
    const OPP = sum(prev, 'opportunities');
    const c = pct(OT, D);
    const comp = pct(D, P);
    const f = pct(FPO, D);
    const dp = Math.round(OPP > 0 ? (DEF / OPP) * 1_000_000 : 0);
    function delta(now, then) {
      return then === 0 ? 0 : Math.round(((now - then) / Math.abs(then)) * 100);
    }
    return {
      completion: delta(completion, comp),
      compliance: delta(compliance, c),
      fpY: delta(fpY, f),
      dPmo: delta(dPmo, dp) * -1,
    };
  }, [data, range, completion, compliance, fpY, dPmo]);

  const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#22c55e", "#f97316", "#a855f7", "#10b981"];

  const defectsByLine = useMemo(() => {
    const g = groupBy(filtered, d => d.line);
    return Object.keys(g).map((k, i) => ({ name: k, value: sum(g[k], 'defects'), color: COLORS[i % COLORS.length] }));
  }, [filtered]);

  // DEV smoke tests
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isProd = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production');
    if (isProd) return;
    try {
      console.assert(Array.isArray(data) && data.length > 0, 'Sample/API data should be present');
      const p0 = allPlants[0];
      if (p0) {
        const countAll = data.length;
        const countPlant = data.filter(d => d.plant === p0).length;
        console.assert(countPlant <= countAll && countPlant > 0, 'Plant-specific subset should be <= total and > 0');
      }
      if (defectPareto.length > 0) {
        const last = defectPareto[defectPareto.length - 1].cumPct;
        console.assert(last >= 99.5 && last <= 100.5, 'Pareto cumulative should end at ~100%');
      }
      console.assert(compliance >= 0 && compliance <= 100, 'Compliance should be 0..100');
      console.assert(fpY >= 0 && fpY <= 100, 'First-Pass Yield should be 0..100');
      console.assert(dPmo >= 0, 'DPMO should be non-negative');
      const narrowFrom = toIso(addDays(new Date(range.to), -3));
      const narrow = data.filter(d => d.date >= narrowFrom && d.date <= range.to);
      console.assert(narrow.length <= data.length, 'Narrowed range should not increase rows');
    } catch (e) {
      console.warn('DEV tests failed:', e);
    }
  }, [data, allPlants, defectPareto, compliance, fpY, dPmo, range.to]);


  const getTotalValues = (name) => {
    const obj = finalData?.totals[0] || {}
    obj[name] = obj[name] || 0
    return obj[name]
  }
  return (
    <div className={`min-h-screen w-full transition-colors duration-300 ${theme === 'dark' ? 'bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-slate-100' : 'bg-gradient-to-b from-gray-100 via-gray-100 to-gray-200 text-gray-900'} p-6`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          {/* <h1 className="text-2xl md:text-3xl font-semibold leading-tight">Quality Control Dashboard</h1> */}
          <p className="opacity-70 text-sm">Digital checksheets • Sampling & periodicity compliance • SPC readiness</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-background/10 hover:bg-background/20 border border-border text-sm"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
          <div className="flex items-center gap-2 bg-background/5 border border-border rounded-xl p-2">
            <Calendar className="w-4 h-4 opacity-80" />
            <input
              type="date"
              value={range.from}
              onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
              className="bg-transparent text-sm outline-none"
            />
            <span className="opacity-60 text-xs">to</span>
            <input
              type="date"
              value={range.to}
              onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
              className="bg-transparent text-sm outline-none"
            />
          </div>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-background/10 hover:bg-background/20 border border-border text-sm"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-3 h-4" />
            <span className="text-sm opacity-80">Filters</span>
          </div>

          <select
            value={filters.Inspection}
            onChange={e => setFilters(f => ({ ...f, Inspection: e.currentTarget.value }))}
            className={`rounded-xl p-2 text-sm border transition-colors ${theme === "dark"
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-800"
              }`}
          >
            <option>All</option>
            {inspectionTypes.map(p => (
              <option key={p.InspectionType}>{p.InspectionType}</option>
            ))}
          </select>

          <select
            value={filters.ItemName}
            onChange={e => setFilters(f => ({ ...f, ItemName: e.currentTarget.value }))}
            className={`rounded-xl p-2 text-sm border transition-colors ${theme === "dark"
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-800"
              }`}
          >
            <option>All</option>
            {items.map(p => (
              <option key={p.ItemName}>{p.ItemName}</option>
            ))}
          </select>

          <select
            value={filters.ItemNumber}
            onChange={e => setFilters(f => ({ ...f, ItemNumber: e.currentTarget.value }))}
            className={`rounded-xl p-2 text-sm border transition-colors ${theme === "dark"
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-800"
              }`}
          >
            <option>All</option>
            {items.map(p => (
              <option key={p.ItemNumber}>{p.ItemNumber}</option>
            ))}
          </select>

          <select
            value={filters.Operation}
            onChange={e => setFilters(f => ({ ...f, Operation: e.currentTarget.value }))}
            className={`rounded-xl p-2 text-sm border transition-colors ${theme === "dark"
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-800"
              }`}
          >
            <option>All</option>
            {items.map(p => (
              <option key={p.Operation}>{p.Operation}</option>
            ))}
          </select>
        </div>
      </Card>


      {/* KPI Row */}
      <div className="grid md:grid-cols-5 gap-4 mb-6">
        <KPI
          icon={ClipboardList}
          label="Planned"
          value={fmt.format(getTotalValues('TotalPlanned'))}
          sublabel="Inspections scheduled"
          trend={deltas.completion}
          trendUp={deltas.completion >= 0}
        />
        <KPI
          icon={CheckCircle2}
          label="Completed"
          value={fmt.format(getTotalValues('TotalCompleted'))}
          // sublabel={`${completion}% of plan`}
          trend={deltas.completion}
          trendUp={deltas.completion >= 0}
        />
        <KPI
          icon={Clock}
          label="On-time Compliance"
          value={`${getTotalValues('OnTimeRate')}`}
          // sublabel={`${fmt.format(onTime)} on time`}
          trend={deltas.compliance}
          trendUp={getTotalValues('OnTimeRate') >= 0}
        />
        <KPI
          icon={TrendingUp}
          label="Pending for Approval"
          value={`${fmt.format(getTotalValues('TotalApprovalPending'))}`}
          // sublabel={`${fmt.format(getTotalValues('TotalApprovalPending'))} `}
          trend={deltas.fpY}
          trendUp={deltas.fpY >= 0}
        />
        <KPI
          icon={AlertTriangle}
          label="Defect Rate"
          value={`${fmt.format(getTotalValues('DefectRate'))}`}
          // sublabel={`${fmt.format(defects)} defects / ${fmt.format(opp)} opps`}
          trend={deltas.dPmo}
          trendUp={true}
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">Plan vs Actual by Line</div>
            <Badge>{filters.plant === 'All' ? 'All Plants' : filters.plant}</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={byLine} margin={{ left: 10, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis dataKey="line" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="planned" name="Planned" fill={theme === 'dark' ? '#334155' : '#94a3b8'} />
                <Bar dataKey="done" name="Done" fill="#22c55e" />
                <Line type="monotone" dataKey="overdue" name="Overdue" stroke="#ef4444" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <div className="font-medium mb-2">Pass Rate & Compliance — Trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byDate} margin={{ left: 10, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => typeof v === 'number' ? `${v}%` : v} />
                <Legend />
                <Line type="monotone" dataKey="passRate" name="Pass Rate" stroke="#22c55e" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="compliance" name="On-time" stroke="#3b82f6" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">Top Overdue — Focus</div>
            <Badge>{overdueList.length} items</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left opacity-70">
                <tr>
                  {/* <th className="py-2 pr-3">Date</th> */}
                  {/* <th className="py-2 pr-3">Plant/Line</th> */}
                  {/* <th className="py-2 pr-3">Component</th> */}
                  <th className="py-2 pr-3">Inspection</th>
                  <th className="py-2 pr-3">Planned</th>
                  <th className="py-2 pr-3">Done</th>
                  <th className="py-2 pr-3">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {overdueList.map(row => (
                  <tr key={row.key} className="border-t border-border">
                    <td className="py-2 pr-3">{row.inspection}</td>
                    <td className="py-2 pr-3">{row.planned}</td>
                    <td className="py-2 pr-3">{row.done}</td>
                    <td className="py-2 pr-3 font-medium text-amber-400">{row.overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card>
          <div className="font-medium mb-2">Distribution — Completion vs Plan</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byDate}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="planned" name="Planned" fill={theme === 'dark' ? '#334155' : '#94a3b8'} />
                <Bar dataKey="done" name="Done" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>



      {/* Footer note */}
      {/* <div className="text-xs opacity-60">
        <p>Definitions: Completion = Done / Planned; On-time Compliance = On-time / Done; First-Pass Yield = First-Pass OK / Done; DPMO = Defects × 1,000,000 / Opportunities (total checkpoints).</p>
        <p className="mt-1">Tip: Use this dashboard to monitor sampling adherence and periodicity, spot lines/components with overdue checks, and drive corrective actions on top Pareto categories.</p>
      </div> */}
    </div>
  );
}


export default QualityDashboard;